import moment from 'moment';
import { translate } from '../../translations';
import { MILITARY_HH_MM } from '../../constants/DateFormats/DateFormats';

export function getMomentFromSeconds(seconds) {
    return moment(seconds * 1000); // time comes from server in epoch time
}

export function timeAgo(time) {
    const momentInTime = getMomentFromSeconds(time);
    const today = moment();
    const yesterday = moment().subtract(1, 'days');

    if (momentInTime.isSame(today, 'day')) {
        return momentInTime.format('h:mm a');
    }
    if (momentInTime.isSame(yesterday, 'day')) {
        return translate('yesterday');
    }
    return momentInTime.format('L');
}

export function messageTime(time) {
    const momentInTime = getMomentFromSeconds(time);
    return momentInTime.format('h:mm a');
}

export function messageSectionDateTime(time) {
    const momentInTime = moment(time);
    return momentInTime.format('MMM. Do, YYYY');
}

export function getToday() {
    return moment();
}

export function getCurrentTime(format = 'h:mm a') {
    return moment().format(format);
}

/**
 * @function Get Date by number and unit (year, month, day) from Today
 *
 * @param {int} number - combined with 'unit' param to get days after today's date (default: 0 days).
 * @param {string} unit - unit of measurement (default: 'days'), refer to link below to see what unit moment accepts.
 *
 * @see {@link https://momentjscom.readthedocs.io/en/latest/moment/03-manipulating/01-add/}
 *
 * @return Return Date in moment format
 */

export function getDaysFromToday({ number = 0, unit = 'days' }) {
    return moment().add(number, unit);
}

export function getDaysBeforeToday({ number = 0, unit = 'days' }) {
    return moment().subtract(number, unit);
}

export function messageSectionDate(time) {
    return time.format('YYYY-MM-DD');
}

export function messageSectionDateFromSeconds(time) {
    const momentInTime = getMomentFromSeconds(time);
    return messageSectionDate(momentInTime);
}

export function messageUnixTimestamp(time) {
    const momentInTime = moment(time);
    return momentInTime.format('X').toString();
}

/**
 * @function Returns Array of Time in set interval
 *      @example ['00:00', '00:30', '01:00', '01:30', ..., '22:30', '23:00', '23:30']
 *
 * @param {Integer} interval -- Set the interval of printed values (in minutes)
 *
 * @return {Array} of times in (HH:mm) format, defaults to 30 minutes
 */
export const getSeriesOfTimesOfDayInMinutes = ({
    interval = 30,
}) => {
    const minutesInADay = 24 * 60;
    const periodInADay = minutesInADay / interval;

    const startTime = moment({ hour: 0, minute: 0, second: 0 });
    const timeLabels = [];
    for (let period = 0; period < periodInADay; period++) {
        timeLabels.push(startTime.format(MILITARY_HH_MM));
        startTime.add({ minutes: interval });
    }

    return timeLabels;
};

/**
 * @function Returns time formatted in (HH:mm) format
 * @param {String} time -- Time to be formatted. Accepted formats: 'HH:mm', 'HH:mm:ss'
 * @return {String} time in (hh:mm AM/PM) format
 *
 * @example
 * formatTime('12:00'); // '12:00 PM'
 */
export const formatTime = (time) => {
    if (time?.indexOf(':') < 1) return '';

    const timeArray = time.split(':');
    if (timeArray.length < 2) return '';
    if (timeArray[0].match(/[^0-9]/) || timeArray[1].match(/[^0-9]/)) return '';

    const hours = parseInt(timeArray[0], 10);
    const minutes = parseInt(timeArray[1], 10);

    if (hours < 0 || hours > 23) return '';
    if (minutes < 0 || minutes > 59) return '';

    const amPm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;

    return `${hours12}:${String(minutes).padStart(2, 0)} ${amPm}`;
};

/**
 * @function estimatedTimeConvert - Returns estimated time converted to minutes or hours
 * @param {String} num -- estimated time to be formatted
 * @return {String} estimated time string (ex. '1 hour' or '30 minutes')
 */
export const estimatedTimeConvert = (num) => {
    let estimatedTimeDisplay = '';
    const hours = Math.floor(num / 60);
    const minutes = num % 60;
    if (hours === 0 && minutes === 0) {
        estimatedTimeDisplay = translate('n_a');
    } else {
        if (hours > 1) {
            estimatedTimeDisplay = `${hours} ${translate('hours')}`;
        } else if (hours === 1) {
            estimatedTimeDisplay = `${hours} ${translate('hour')}`;
        }
        if (minutes > 1) {
            estimatedTimeDisplay = `${estimatedTimeDisplay} ${minutes} ${translate('minutes')}`;
        } else if (minutes === 1) {
            estimatedTimeDisplay = `${estimatedTimeDisplay} ${minutes} ${translate('minute')}`;
        }
    }
    return estimatedTimeDisplay;
};
